<script setup>
import { reactive, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { uploadFile } from 'dashboard/helper/uploadHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import LinhaPadrao from 'dashboard/components-next/captain/pageComponents/assistant/settings/LinhaPadrao.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
  // 5 abas (02/09/2026): 'identidade' mostra frases prontas, briefing e nome de
  // exibição (aba Quem é); 'tecnico' mostra o resto (aba Técnico); 'todas' é o
  // formulário inteiro, como sempre foi. O estado e o salvar são os mesmos.
  secao: {
    type: String,
    default: 'todas',
    validator: v => ['todas', 'identidade', 'tecnico'].includes(v),
  },
});

const emit = defineEmits(['submit']);
const mostraIdentidade = computed(() => props.secao !== 'tecnico');
const mostraTecnico = computed(() => props.secao !== 'identidade');

const { t } = useI18n();
const store = useStore();

// Caixas vinculadas a ESTE assistente (mesma fonte do OnboardingChecklist:
// GET captain/assistants/:id/inboxes → partial completo de inbox, com
// channel_type/provider/message_templates).
const captainInboxes = useMapGetter('captainInboxes/getRecords');

const initialState = {
  agentDisplayName: '',
  showAgentName: false,
  handoffMessage: '',
  resolutionMessage: '',
  instructions: '',
  temperature: 1,
  debounceSeconds: 8,
  followupEnabled: false,
  followupSteps: [],
  followupTemplates: [],
  followupTemplateOrder: 'sequential',
  voiceReplyEnabled: false,
  qualityEvalEnabled: false,
  savingsPercent: '',
  loopGuardMaxReplies: 0,
  faqTopK: 5,
  faqMaxDistance: 0.65,
  crossConversationMemory: true,
};

const state = reactive({ ...initialState });

// Aba Técnico (fatia 5 das 5 abas, 02/09/2026): todo campo técnico mostra o
// padrão e tem "voltar ao padrão"; o botão do topo volta tudo de uma vez, com
// confirmação listando o que muda. Os padrões espelham os do backend.
const PADROES = {
  temperature: 1,
  debounceSeconds: 8,
  voiceReplyEnabled: false,
  qualityEvalEnabled: false,
  crossConversationMemory: true,
  savingsPercent: '',
  loopGuardMaxReplies: 0,
  faqTopK: 5,
  faqMaxDistance: 0.65,
};
const ROTULOS_PADRAO = {
  temperature: 'Criatividade',
  debounceSeconds: 'Tempo de espera',
  voiceReplyEnabled: 'Responder em áudio',
  qualityEvalEnabled: 'Auto-avaliar respostas',
  crossConversationMemory: 'Lembrar conversas anteriores',
  savingsPercent: 'Desconto padrão',
  loopGuardMaxReplies: 'Limite de respostas',
  faqTopK: 'FAQs por pergunta',
  faqMaxDistance: 'Rigor da busca',
};
const foraDoPadrao = campo => String(state[campo]) !== String(PADROES[campo]);
const restaurar = campo => {
  state[campo] = PADROES[campo];
};
const camposForaDoPadrao = computed(() =>
  Object.keys(PADROES).filter(foraDoPadrao)
);
const confirmaPadraoDialog = ref(null);
const pedirRestaurarTudo = () => confirmaPadraoDialog.value?.open();
const restaurarTudo = () => {
  Object.keys(PADROES).forEach(restaurar);
  confirmaPadraoDialog.value?.close();
};
const listaForaDoPadrao = computed(() =>
  camposForaDoPadrao.value.map(c => ROTULOS_PADRAO[c]).join(', ')
);

const validationRules = {
  handoffMessage: { minLength: minLength(1) },
  resolutionMessage: { minLength: minLength(1) },
  instructions: { minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const formErrors = computed(() => ({
  handoffMessage: getErrorMessage('handoffMessage'),
  resolutionMessage: getErrorMessage('resolutionMessage'),
  instructions: getErrorMessage('instructions'),
}));

// Campos específicos de nicho só aparecem quando a tool correspondente está
// habilitada para ESTE agente. O "% de economia" serve só à tool calculate_economy
// (Plus Energy/solar) : não deve poluir o settings de clientes de outros nichos.
const hasEconomyTool = computed(() =>
  (props.assistant?.effective_tool_ids || []).includes('calculate_economy')
);

// Um passo novo do follow-up (default = inteligente, 24h após o anterior).
const makeStep = (overrides = {}) => ({
  type: 'smart',
  delayHours: 24,
  prompt: '',
  message: '',
  media: null,
  uploading: false,
  ...overrides,
});

// config.followup_steps -> lista de passos da UI. Aceita o formato NOVO (objetos)
// e cai num passo único pros formatos antigos (string/array de strings / smart).
const parseFollowupSteps = config => {
  const raw = config.followup_steps;
  if (Array.isArray(raw) && raw.some(s => s && typeof s === 'object')) {
    return raw
      .filter(s => s && typeof s === 'object')
      .map(s =>
        makeStep({
          type: s.type === 'manual' ? 'manual' : 'smart',
          delayHours: Number(s.delay_hours) || 24,
          prompt: s.prompt || '',
          message: s.message || '',
          media: s.media && s.media.url ? { ...s.media } : null,
        })
      );
  }
  // Compat: cadência antiga (strings) ou mensagem única -> passos manuais; senão 1 smart.
  const delay = Number(config.followup_delay_hours) || 24;
  if (Array.isArray(raw) && raw.some(s => typeof s === 'string' && s.trim())) {
    const smart = config.followup_smart === true;
    return raw
      .filter(s => typeof s === 'string' && s.trim())
      .map(m =>
        makeStep({
          type: smart ? 'smart' : 'manual',
          delayHours: delay,
          message: m,
        })
      );
  }
  if (config.followup_message && !config.followup_smart) {
    return [
      makeStep({
        type: 'manual',
        delayHours: delay,
        message: config.followup_message,
      }),
    ];
  }
  return [
    makeStep({
      type: 'smart',
      delayHours: delay,
      prompt: config.followup_prompt || '',
    }),
  ];
};

const serializeFollowupSteps = () =>
  state.followupSteps.map(s => ({
    type: s.type === 'manual' ? 'manual' : 'smart',
    delay_hours: Number(s.delayHours) || 24,
    message: s.type === 'manual' ? s.message : '',
    prompt: s.type === 'smart' ? s.prompt : '',
    media: s.type === 'manual' && s.media && s.media.url ? s.media : null,
  }));

// SiteUp (janela de 24h): templates da API OFICIAL usados pelo FollowUpJob
// quando a janela de 24h está fechada (mensagem livre não sai; template
// aprovado sai). A UI seleciona por NOME (string); se o config trouxe um
// objeto { name, processed_params } (configurado por fora), preservamos o
// objeto no save pra não perder os parâmetros.
let savedTemplateEntries = {};

// config.followup_templates (strings ou objetos) -> nomes selecionados na UI.
const parseFollowupTemplates = config => {
  savedTemplateEntries = {};
  const raw = Array.isArray(config.followup_templates)
    ? config.followup_templates
    : [];
  const names = [];
  raw.forEach(entry => {
    const name =
      typeof entry === 'string'
        ? entry.trim()
        : String((entry && entry.name) || '').trim();
    if (!name || names.includes(name)) return;
    names.push(name);
    if (entry && typeof entry === 'object') savedTemplateEntries[name] = entry;
  });
  return names;
};

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.agentDisplayName = config.agent_display_name || '';
  state.showAgentName = config.show_agent_name === true;
  state.handoffMessage = config.handoff_message;
  state.resolutionMessage = config.resolution_message;
  state.instructions = config.instructions;
  // ?? (não ||): temperatura 0 ("mais preciso") e espera 0 são valores VÁLIDOS :
  // com || a tela mentia mostrando 1 e 8 ao reabrir.
  state.temperature = config.temperature ?? 1;
  state.debounceSeconds = config.autonomous_debounce_seconds ?? 8;
  state.followupEnabled = config.followup_enabled === true;
  state.followupSteps = parseFollowupSteps(config);
  state.followupTemplates = parseFollowupTemplates(config);
  state.followupTemplateOrder =
    config.followup_template_order === 'random' ? 'random' : 'sequential';
  state.voiceReplyEnabled = config.voice_reply_enabled === true;
  state.qualityEvalEnabled = config.quality_eval_enabled === true;
  state.savingsPercent = config.savings_percent ?? '';
  state.loopGuardMaxReplies = config.loop_guard_max_replies || 0;
  state.faqTopK = config.faq_top_k || 5;
  state.faqMaxDistance = config.faq_max_distance ?? 0.65;
  state.crossConversationMemory = config.cross_conversation_memory !== false;
};

const serializeFollowupTemplates = () =>
  state.followupTemplates.map(name => savedTemplateEntries[name] || name);

// Caixas da API oficial do WhatsApp vinculadas ao assistente.
const officialInboxes = computed(() =>
  (captainInboxes.value || []).filter(
    inbox =>
      inbox.channel_type === 'Channel::Whatsapp' &&
      inbox.provider === 'whatsapp_cloud'
  )
);
const hasOfficialInbox = computed(() => officialInboxes.value.length > 0);

// Templates APROVADOS das caixas oficiais, dedup por nome (o backend casa por
// nome). Mais de um idioma com o mesmo nome = um item só, idiomas agrupados.
const approvedOfficialTemplates = computed(() => {
  const byName = new Map();
  officialInboxes.value.forEach(inbox => {
    const templates = Array.isArray(inbox.message_templates)
      ? inbox.message_templates
      : [];
    templates.forEach(tpl => {
      if (!tpl || !tpl.name) return;
      if (String(tpl.status || '').toLowerCase() !== 'approved') return;
      const existing = byName.get(tpl.name);
      if (existing) {
        if (tpl.language && !existing.languages.includes(tpl.language)) {
          existing.languages.push(tpl.language);
        }
      } else {
        byName.set(tpl.name, {
          name: tpl.name,
          languages: tpl.language ? [tpl.language] : [],
        });
      }
    });
  });
  return Array.from(byName.values()).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

// Selecionados que não existem mais (ou perderam aprovação) na(s) caixa(s):
// continuam listados com aviso, marcados, pra dar pra desmarcar/remover.
const missingFollowupTemplates = computed(() =>
  state.followupTemplates.filter(
    name => !approvedOfficialTemplates.value.some(tpl => tpl.name === name)
  )
);

const addStep = () => {
  state.followupSteps.push(makeStep());
};

const removeStep = index => {
  state.followupSteps.splice(index, 1);
};

const removeStepMedia = step => {
  step.media = null;
};

const tipoDoArquivo = file => {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  return 'file';
};

// Upload na hora da imagem/arquivo do passo manual.
const handleStepFile = async (step, event) => {
  const file = event.target?.files?.[0];
  if (!file) return;
  step.uploading = true;
  try {
    const { fileUrl } = await uploadFile(file);
    const fileType = tipoDoArquivo(file);
    step.media = { url: fileUrl, file_type: fileType, filename: file.name };
  } catch (e) {
    useAlert('Não foi possível enviar o arquivo.');
  } finally {
    step.uploading = false;
    if (event.target) event.target.value = '';
  }
};

const handleSystemMessagesUpdate = async () => {
  const result = await Promise.all([
    v$.value.handoffMessage.$validate(),
    v$.value.resolutionMessage.$validate(),
    v$.value.instructions.$validate(),
  ]).then(results => results.every(Boolean));
  if (!result) return;

  const payload = {
    config: {
      ...props.assistant.config,
      agent_display_name: state.agentDisplayName,
      show_agent_name: state.showAgentName,
      handoff_message: state.handoffMessage,
      resolution_message: state.resolutionMessage,
      instructions: state.instructions,
      // 0 é válido (temperatura "mais preciso" / espera imediata) : || virava 1 e 8
      temperature:
        state.temperature === '' || state.temperature === null
          ? 1
          : Number(state.temperature),
      autonomous_debounce_seconds:
        state.debounceSeconds === '' || state.debounceSeconds === null
          ? 8
          : Number(state.debounceSeconds),
      // Follow-up = sequência de passos (objetos). Cada passo: tipo (smart/manual),
      // tempo em cascata (delay_hours após o passo anterior) e conteúdo (orientação
      // p/ smart; mensagem + mídia enviada p/ manual). Ver Captain::FollowUpJob.
      followup_enabled: state.followupEnabled,
      followup_steps: serializeFollowupSteps(),
      // Janela de 24h fechada (API oficial): o resgate sai como um destes
      // templates aprovados, em rodízio ou aleatório. Ver Captain::FollowUpJob.
      followup_templates: serializeFollowupTemplates(),
      followup_template_order:
        state.followupTemplateOrder === 'random' ? 'random' : 'sequential',
      voice_reply_enabled: state.voiceReplyEnabled,
      quality_eval_enabled: state.qualityEvalEnabled,
      savings_percent:
        state.savingsPercent === '' ? null : Number(state.savingsPercent),
      loop_guard_max_replies: Number(state.loopGuardMaxReplies) || 0,
      faq_top_k: Number(state.faqTopK) || 5,
      faq_max_distance:
        state.faqMaxDistance === '' ? null : Number(state.faqMaxDistance),
      cross_conversation_memory: state.crossConversationMemory,
    },
  };

  emit('submit', payload);
};

// Carrega as caixas vinculadas (uma vez por assistente) pra detectar API
// oficial + listar os templates. O OnboardingChecklist da mesma página também
// busca, mas o form não depende dele (best-effort, falha silenciosa).
let fetchedInboxesForAssistant = null;

watch(
  () => props.assistant,
  newAssistant => {
    if (!newAssistant) return;
    updateStateFromAssistant(newAssistant);
    if (newAssistant.id && fetchedInboxesForAssistant !== newAssistant.id) {
      fetchedInboxesForAssistant = newAssistant.id;
      store
        .dispatch('captainInboxes/get', { assistantId: newAssistant.id })
        .catch(() => {});
    }
  },
  { immediate: true }
);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- Textos em português direto no template são dívida anterior a 02/09/2026; a
     fatia 5 das abas (Técnico) reescreve esta parte e leva tudo para o i18n. -->
<template>
  <div class="flex flex-col gap-6">
    <template v-if="mostraIdentidade">
      <h3 class="text-sm font-semibold text-n-slate-12">
        Mensagens automáticas
      </h3>
      <Editor
        v-model="state.handoffMessage"
        :label="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_MESSAGE.LABEL')"
        :placeholder="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_MESSAGE.PLACEHOLDER')"
        :message="formErrors.handoffMessage"
        :message-type="formErrors.handoffMessage ? 'error' : 'info'"
        class="z-0"
      />

      <Editor
        v-model="state.resolutionMessage"
        :label="t('CAPTAIN.ASSISTANTS.FORM.RESOLUTION_MESSAGE.LABEL')"
        :placeholder="
          t('CAPTAIN.ASSISTANTS.FORM.RESOLUTION_MESSAGE.PLACEHOLDER')
        "
        :message="formErrors.resolutionMessage"
        :message-type="formErrors.resolutionMessage ? 'error' : 'info'"
        class="z-0"
      />

      <h3
        class="text-sm font-semibold text-n-slate-12 pt-4 border-t border-n-weak"
      >
        Personalidade
      </h3>
      <div class="flex flex-col gap-2">
        <Editor
          v-model="state.instructions"
          :label="t('CAPTAIN.ASSISTANTS.FORM.INSTRUCTIONS.LABEL')"
          :placeholder="t('CAPTAIN.ASSISTANTS.FORM.INSTRUCTIONS.PLACEHOLDER')"
          :message="formErrors.instructions"
          :max-length="200000"
          :message-type="formErrors.instructions ? 'error' : 'info'"
          class="z-0"
        />
        <p class="text-sm text-n-slate-11 italic">
          Prompt principal do agente (personalidade, regras, fluxo). É o que
          mais muda o comportamento da IA. Edite com cuidado.
        </p>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Nome de exibição da IA
        </label>
        <input
          v-model="state.agentDisplayName"
          type="text"
          :placeholder="assistant.name || 'Ex.: Gabriela'"
          class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
        />
        <label
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
        >
          <input v-model="state.showAgentName" type="checkbox" />
          Mostrar o nome da IA antes das mensagens
        </label>
        <p class="text-sm text-n-slate-11 italic">
          Quando ligado, cada resposta começa com o nome em negrito, ex.:
          <strong>*Gabriela:*</strong> Olá! Vazio = usa o nome do assistente.
          Desligado por padrão.
        </p>
      </div>
    </template>

    <template v-if="mostraTecnico">
      <div
        class="flex items-center justify-between gap-3 pt-4 border-t border-n-weak"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">Comportamento</h3>
        <button
          v-if="camposForaDoPadrao.length"
          type="button"
          class="text-xs font-medium text-n-slate-11 underline"
          data-testid="voltar-tudo-ao-padrao"
          @click="pedirRestaurarTudo"
        >
          Voltar tudo ao padrão ({{ camposForaDoPadrao.length }})
        </button>
      </div>
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAPTAIN.ASSISTANTS.FORM.TEMPERATURE.LABEL') }}
        </label>
        <div class="flex items-center gap-3">
          <span class="text-xs text-n-slate-11 shrink-0">Mais preciso</span>
          <input
            v-model="state.temperature"
            type="range"
            min="0"
            max="1"
            step="0.1"
            class="w-full"
          />
          <span class="text-xs text-n-slate-11 shrink-0">Mais criativo</span>
          <span class="text-sm text-n-slate-12 w-8 text-right">{{
            state.temperature
          }}</span>
        </div>
        <p class="text-sm text-n-slate-11 italic">
          {{ t('CAPTAIN.ASSISTANTS.FORM.TEMPERATURE.DESCRIPTION') }}
        </p>
        <LinhaPadrao
          :fora="foraDoPadrao('temperature')"
          padrao="1 (equilíbrio entre fiel e criativo)"
          @restaurar="restaurar('temperature')"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Tempo de espera antes de responder (segundos)
        </label>
        <input
          v-model="state.debounceSeconds"
          type="number"
          min="0"
          max="120"
          class="w-32 px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
        />
        <p class="text-sm text-n-slate-11 italic">
          A IA espera esse tempo após a última mensagem do cliente e responde
          uma vez só, agrupando mensagens enviadas em sequência (várias linhas).
          Padrão: 8s.
        </p>
        <LinhaPadrao
          :fora="foraDoPadrao('debounceSeconds')"
          padrao="8 segundos"
          @restaurar="restaurar('debounceSeconds')"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
        >
          <input v-model="state.voiceReplyEnabled" type="checkbox" />
          Responder em áudio quando o cliente mandar áudio
        </label>
        <p class="text-sm text-n-slate-11 italic">
          Se o cliente enviar um áudio, a IA responde também em áudio (voz
          sintetizada). Se a síntese falhar, cai pro texto. Desligado por
          padrão.
        </p>
        <LinhaPadrao
          :fora="foraDoPadrao('voiceReplyEnabled')"
          padrao="desligado"
          @restaurar="restaurar('voiceReplyEnabled')"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
        >
          <input v-model="state.qualityEvalEnabled" type="checkbox" />
          Auto-avaliar a qualidade das respostas da IA
        </label>
        <p class="text-sm text-n-slate-11 italic">
          Após cada resposta, uma 2ª IA dá uma nota de 0 a 100 (com motivo) pra
          você acompanhar a qualidade no Painel da IA. Consome créditos extras.
          Desligado por padrão.
        </p>
        <LinhaPadrao
          :fora="foraDoPadrao('qualityEvalEnabled')"
          padrao="desligado"
          @restaurar="restaurar('qualityEvalEnabled')"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
        >
          <input v-model="state.crossConversationMemory" type="checkbox" />
          Lembrar conversas anteriores do contato
        </label>
        <p class="text-sm text-n-slate-11 italic">
          A IA recebe um resumo das últimas conversas do mesmo contato (data,
          assuntos e o que o cliente pediu) para dar continuidade: cliente
          recorrente não precisa repetir tudo. Só leitura, sem custo extra.
          Ligado por padrão.
        </p>
        <LinhaPadrao
          :fora="foraDoPadrao('crossConversationMemory')"
          padrao="ligado"
          @restaurar="restaurar('crossConversationMemory')"
        />
      </div>

      <div v-if="hasEconomyTool" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Desconto/economia padrão (%)
        </label>
        <input
          v-model="state.savingsPercent"
          type="number"
          min="0"
          max="100"
          placeholder="25"
          class="w-32 px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
        />
        <p class="text-sm text-n-slate-11 italic">
          Usado pela tool "Calcular economia" somente sobre o valor elegível de
          energia, sem impostos e taxas. Sem esse valor separado, a tool mostra
          apenas um teto de referência. Vazio = 25%.
        </p>
        <LinhaPadrao
          :fora="foraDoPadrao('savingsPercent')"
          padrao="vazio (25%)"
          @restaurar="restaurar('savingsPercent')"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          Limite de respostas antes de escalar para humano
        </label>
        <input
          v-model="state.loopGuardMaxReplies"
          type="number"
          min="0"
          max="50"
          class="w-32 px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
        />
        <p class="text-sm text-n-slate-11 italic">
          Se a IA responder essa quantidade de vezes e a conversa continuar sem
          resolver, ela passa para o atendente humano (com resumo). 0 =
          desligado.
        </p>
        <LinhaPadrao
          :fora="foraDoPadrao('loopGuardMaxReplies')"
          padrao="0 (desligado)"
          @restaurar="restaurar('loopGuardMaxReplies')"
        />
      </div>

      <h3
        class="text-sm font-semibold text-n-slate-12 pt-4 border-t border-n-weak"
      >
        Base de conhecimento
      </h3>
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          FAQ: busca semântica
        </label>
        <div class="flex items-end gap-4">
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11">FAQs por pergunta</label>
            <input
              v-model="state.faqTopK"
              type="number"
              min="1"
              max="20"
              class="w-28 px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-xs text-n-slate-11"
              >Rigor (distância máxima de 0 a 1)</label
            >
            <input
              v-model="state.faqMaxDistance"
              type="number"
              min="0"
              max="1"
              step="0.05"
              class="w-28 px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
            />
          </div>
        </div>
        <p class="text-sm text-n-slate-11 italic">
          Quantas FAQs a IA consulta por pergunta e quão "perto" elas precisam
          estar do tema para serem usadas. Menor = mais rígido (só FAQs muito
          próximas); padrão 0.65. Se nada relevante for achado, a IA não
          inventa: confirma ou passa pro humano.
        </p>
        <LinhaPadrao
          :fora="foraDoPadrao('faqTopK')"
          padrao="5 FAQs por pergunta"
          @restaurar="restaurar('faqTopK')"
        />
        <LinhaPadrao
          :fora="foraDoPadrao('faqMaxDistance')"
          padrao="0.65 de rigor"
          @restaurar="restaurar('faqMaxDistance')"
        />
      </div>

      <h3
        class="text-sm font-semibold text-n-slate-12 pt-4 border-t border-n-weak"
      >
        Follow-up
      </h3>
      <div class="flex flex-col gap-2">
        <label
          class="flex items-center gap-2 text-sm font-medium text-n-slate-12"
        >
          <input v-model="state.followupEnabled" type="checkbox" />
          Follow-up automático (reengajar lead que sumiu)
        </label>
        <p class="text-sm text-n-slate-11 italic">
          Monte uma sequência de passos pra reengajar quem sumiu. Cada passo
          pode ser
          <strong>inteligente</strong> (a IA lê o histórico e escreve) ou
          <strong>manual</strong> (sua mensagem + imagem/arquivo), e dispara um
          tempo depois do passo anterior. Desligado por padrão.
        </p>

        <div v-if="state.followupEnabled" class="flex flex-col gap-3">
          <div
            v-for="(step, index) in state.followupSteps"
            :key="index"
            class="flex flex-col gap-2 p-3 rounded-xl border border-n-weak bg-n-alpha-1"
          >
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12"
                >Passo {{ index + 1 }}</span
              >
              <button
                type="button"
                class="text-xs text-n-ruby-11 hover:underline"
                @click="removeStep(index)"
              >
                Remover
              </button>
            </div>

            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-xs text-n-slate-11">Disparar</span>
              <input
                v-model="step.delayHours"
                type="number"
                min="1"
                max="336"
                class="w-20 px-2 py-1 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
              />
              <span class="text-xs text-n-slate-11">
                horas
                {{
                  index === 0
                    ? 'depois que o cliente sumiu'
                    : 'depois do passo anterior'
                }}
              </span>
            </div>

            <div class="flex items-center gap-4">
              <label class="flex items-center gap-1.5 text-sm text-n-slate-12">
                <input v-model="step.type" type="radio" value="smart" />
                Inteligente
              </label>
              <label class="flex items-center gap-1.5 text-sm text-n-slate-12">
                <input v-model="step.type" type="radio" value="manual" />
                Manual
              </label>
            </div>

            <textarea
              v-if="step.type === 'smart'"
              v-model="step.prompt"
              rows="2"
              placeholder="Orientações (opcional): como a IA deve reativar. Vazio = usa a persona do agente."
              class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
            />

            <div v-else class="flex flex-col gap-2">
              <textarea
                v-model="step.message"
                rows="2"
                placeholder="Sua mensagem. Variáveis: {nome}, {email}, {tel}."
                class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-sm"
              />
              <div class="flex items-center gap-2 flex-wrap">
                <label
                  class="inline-flex items-center gap-1.5 text-xs text-n-slate-11 cursor-pointer px-2 py-1 rounded-md border border-n-weak hover:bg-n-alpha-2"
                >
                  <span class="i-ri-attachment-line size-3.5" />
                  {{ step.uploading ? 'Enviando…' : 'Anexar imagem/arquivo' }}
                  <input
                    type="file"
                    class="hidden"
                    :disabled="step.uploading"
                    @change="handleStepFile(step, $event)"
                  />
                </label>
                <span
                  v-if="step.media"
                  class="inline-flex items-center gap-1 text-xs text-n-slate-11"
                >
                  <span class="i-ri-check-line size-3.5 text-n-teal-11" />
                  {{ step.media.filename }}
                  <button
                    type="button"
                    class="i-ri-close-line size-3.5 text-n-slate-10 hover:text-n-ruby-11"
                    @click="removeStepMedia(step)"
                  />
                </span>
              </div>
            </div>
          </div>

          <Button
            label="Adicionar passo"
            variant="faded"
            color="slate"
            size="sm"
            icon="i-ri-add-line"
            @click="addStep"
          />

          <!-- SiteUp: só aparece quando alguma caixa vinculada é da API oficial
             (Channel::Whatsapp provider whatsapp_cloud). -->
          <div
            v-if="hasOfficialInbox"
            class="flex flex-col gap-2 p-3 rounded-xl border border-n-weak bg-n-alpha-1"
          >
            <span class="text-sm font-medium text-n-slate-12">
              Templates da API oficial (janela de 24h fechada)
            </span>
            <p class="text-sm text-n-slate-11 italic">
              Fora da janela de 24h do WhatsApp oficial, o resgate sai como um
              destes templates aprovados : a IA não escreve texto livre nesse
              caso. Sem template selecionado, o follow-up não é enviado com a
              janela fechada.
            </p>

            <p
              v-if="
                !approvedOfficialTemplates.length &&
                !missingFollowupTemplates.length
              "
              class="text-sm text-n-slate-11"
            >
              Nenhum template aprovado encontrado na(s) caixa(s) da API oficial.
            </p>
            <div v-else class="flex flex-col gap-1.5">
              <label
                v-for="tpl in approvedOfficialTemplates"
                :key="tpl.name"
                class="flex items-center gap-2 text-sm text-n-slate-12"
              >
                <input
                  v-model="state.followupTemplates"
                  type="checkbox"
                  :value="tpl.name"
                />
                {{ tpl.name }}
                <span
                  v-if="tpl.languages.length"
                  class="text-xs text-n-slate-11"
                >
                  ({{ tpl.languages.join(', ') }})
                </span>
              </label>
              <label
                v-for="name in missingFollowupTemplates"
                :key="`missing-${name}`"
                class="flex items-center gap-2 text-sm text-n-slate-12"
              >
                <input
                  v-model="state.followupTemplates"
                  type="checkbox"
                  :value="name"
                />
                {{ name }}
                <span class="text-xs text-n-amber-11">
                  (não encontrado na caixa)
                </span>
              </label>
            </div>

            <div
              v-if="state.followupTemplates.length"
              class="flex items-center gap-4 flex-wrap pt-1"
            >
              <span class="text-xs text-n-slate-11">Ordem de envio:</span>
              <label class="flex items-center gap-1.5 text-sm text-n-slate-12">
                <input
                  v-model="state.followupTemplateOrder"
                  type="radio"
                  value="sequential"
                />
                Em ordem (rodízio)
              </label>
              <label class="flex items-center gap-1.5 text-sm text-n-slate-12">
                <input
                  v-model="state.followupTemplateOrder"
                  type="radio"
                  value="random"
                />
                Aleatório
              </label>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleSystemMessagesUpdate"
      />
    </div>
    <Dialog
      ref="confirmaPadraoDialog"
      type="alert"
      title="Voltar tudo ao padrão?"
      :description="`Volta ao padrão: ${listaForaDoPadrao}. O briefing, as frases prontas e as caixas não mudam. Salve depois para valer.`"
      confirm-button-label="Sim, voltar tudo"
      cancel-button-label="Cancelar"
      @confirm="restaurarTudo"
    />
  </div>
</template>
